import React, { useState, useEffect, useMemo } from "react";
import { fetchApi } from "../../apis/Index";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    orders: 0,
    products: 0,
    users: 0,
    revenue: 0,
    pending: 0,
    delivered: 0,
    loading: true,
    ordersList: [],
  });

  useEffect(() => {
    async function loadStats() {
      try {
        const [ordersRes, productsRes, usersRes] = await Promise.all([
          fetchApi("https://wittynailtip.com/backend/admin-orders.php"),
          fetchApi("https://wittynailtip.com/backend/product.php"),
          fetchApi("https://wittynailtip.com/backend/admin-stats.php")
        ]);
        
        const ordersList = ordersRes.data.success ? ordersRes.data.orders : [];
        const productsCount = productsRes.data.status === 'success' ? productsRes.data.data.length : 0;
        const usersCount = usersRes.data.success ? usersRes.data.total_users : 0;

        let totalRevenue = 0;
        let totalPending = 0;
        let totalDelivered = 0;

        ordersList.forEach(order => {
          totalRevenue += parseFloat(order.price || 0);
          if (order.tracking === 'paid' || order.tracking === 'shipped') {
            totalPending += 1;
          } else if (order.tracking === 'delivered') {
            totalDelivered += 1;
          }
        });

        setStats({
          orders: ordersList.length,
          products: productsCount,
          users: usersCount,
          revenue: totalRevenue,
          pending: totalPending,
          delivered: totalDelivered,
          ordersList: ordersList,
          loading: false
        });
      } catch (error) {
        setStats(prev => ({ ...prev, loading: false }));
      }
    }
    loadStats();
  }, []);

  // Compute daily revenue for the chart (last 7 days)
  const chartData = useMemo(() => {
    if (!stats.ordersList || stats.ordersList.length === 0) return [];
    
    const dataMap = {};
    // Initialize last 7 days with zero revenue
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      dataMap[dateStr] = { date: dateStr, revenue: 0 };
    }

    stats.ordersList.forEach(order => {
      // Safely extract the date part (assuming created_at is YYYY-MM-DD HH:MM:SS or similar)
      const orderDateStr = order.created_at || order.order_date;
      if(orderDateStr){
        try {
          const dateStr = orderDateStr.split(' ')[0]; // Gets YYYY-MM-DD
          if (dataMap[dateStr] !== undefined) {
            dataMap[dateStr].revenue += parseFloat(order.price || 0);
          }
        } catch(e) {}
      }
    });

    // Format dates nicely for X axis
    return Object.values(dataMap).map(item => {
      // Fix timezone offset issues by parsing date string directly
      const [year, month, day] = item.date.split('-');
      const d = new Date(year, month - 1, day);
      return {
        name: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        Revenue: item.revenue
      };
    });
  }, [stats.ordersList]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-100 shadow-lg rounded-xl">
          <p className="text-gray-500 text-xs mb-1">{label}</p>
          <p className="text-gray-900 font-bold text-sm">
            Revenue: ${payload[0].value.toFixed(2)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard Overview</h1>
          <p className="text-gray-500 mt-1">Welcome back to the WittyNails command center.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-6">
        <div className="bg-white rounded-3xl p-5 lg:p-6 shadow-sm border border-gray-100 flex flex-col justify-between col-span-2 md:col-span-1 lg:col-span-2">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-600">Total Revenue</h3>
            <div className="w-10 h-10 bg-[#E1F7EB] rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-[#20AA5F]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
            </div>
          </div>
          <p className="text-3xl lg:text-4xl font-bold text-gray-900">${stats.loading ? "--" : stats.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        </div>

        <div className="bg-white rounded-3xl p-5 lg:p-6 shadow-sm border border-gray-100 flex flex-col justify-between col-span-1">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-600">All Orders</h3>
            <div className="w-10 h-10 bg-[#fff1f5] rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-[#FFB7CF]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path></svg>
            </div>
          </div>
          <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats.loading ? "--" : stats.orders}</p>
        </div>

        <div className="bg-white rounded-3xl p-5 lg:p-6 shadow-sm border border-gray-100 flex flex-col justify-between col-span-1">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-600">Pending</h3>
            <div className="w-10 h-10 bg-yellow-50 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
            </div>
          </div>
          <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats.loading ? "--" : stats.pending}</p>
        </div>

        <div className="bg-white rounded-3xl p-5 lg:p-6 shadow-sm border border-gray-100 flex flex-col justify-between col-span-1">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-600">Delivered</h3>
            <div className="w-10 h-10 bg-green-50 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
            </div>
          </div>
          <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats.loading ? "--" : stats.delivered}</p>
        </div>

        <div className="bg-white rounded-3xl p-5 lg:p-6 shadow-sm border border-gray-100 flex flex-col justify-between col-span-1">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-600 text-sm lg:text-base whitespace-nowrap">Users</h3>
            <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
            </div>
          </div>
          <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats.loading ? "--" : stats.users}</p>
        </div>
      </div>

      <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900">Revenue Analytics</h2>
          <p className="text-sm text-gray-500">Your revenue over the last 7 days</p>
        </div>
        
        <div className="w-full h-[300px]">
          {stats.loading ? (
            <div className="w-full h-full flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FFB7CF]"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FFB7CF" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#FFB7CF" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                <XAxis 
                  dataKey="name" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fill: '#9ca3af', fontSize: 12 }}
                  dy={10}
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fill: '#9ca3af', fontSize: 12 }}
                  tickFormatter={(value) => `$${value}`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area 
                  type="monotone" 
                  dataKey="Revenue" 
                  stroke="#FFB7CF" 
                  strokeWidth={3}
                  fillOpacity={1} 
                  fill="url(#colorRevenue)" 
                  activeDot={{ r: 6, strokeWidth: 0, fill: '#FFB7CF' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
